import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { SerializationContext, DataModelBuilder, SerializedData, isSameData } from '../dto/dto.js';
import { NotificationEvent, SyncEventSource } from '../sync-event.js';
import { PoolCheckIn } from './pool-check-in.js';

export class PoolCheckInEvent extends NotificationEvent {
  static readonly TYPE_ID: string = '8722905bbc7a4162a52480f913cac858';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly seen: boolean,
    public readonly checkInId: string,
    public readonly checkIn: PoolCheckIn,
  ) {
    super();
  }

  get $typeId(): string {
    return PoolCheckInEvent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, PoolCheckInEvent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): PoolCheckInEvent {
    return new PoolCheckInEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        MapUtils.get<boolean>(map, 'seen'),
        MapUtils.get<string>(map, 'checkInId'),
        context.fromData<PoolCheckIn>(MapUtils.get<SerializedData>(map, 'checkIn')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'seen': this.seen,
      'checkInId': this.checkInId,
      'checkIn': context.toData(this.checkIn),
    };
  }

  clone(): this {
    return new PoolCheckInEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.seen,
        this.checkInId,
        this.checkIn.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.seen === this.seen &&
        other.checkInId === this.checkInId &&
        isSameData(other.checkIn, this.checkIn);
  }

  markAsSeen(): this {
    return new PoolCheckInEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        true,
        this.checkInId,
        this.checkIn.clone(),
    ) as this;
  }
}

export class PoolCheckInEventBuilder extends DataModelBuilder<PoolCheckInEvent> {
  build(context: SerializationContext, map: SerializedData): PoolCheckInEvent {
    return PoolCheckInEvent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(PoolCheckInEvent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(PoolCheckInEvent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `deviceId` of type: string | null
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
